import React from 'react';
import { formatKeyMetrics } from './formBuilder/utility/renderValue';
import classNames from 'classnames';
import Tooltip from './Tooltip';

export default function KeyMetrics({
  schema,
  data,
  classes,
  layoutClasses = '',
}) {
  const columnLayout = schema[0]?.layout === 'column';

  return (
    <dl
      className={classNames(
        'w-full h-full space-y-5 col-span-4 mb-10',
        columnLayout && 'sm:mt-[2.8rem] sm:col-span-1'
      )}
    >
      {schema.map((section, index) => (
        <div
          key={section.sectionValue}
          className={classNames(
            'sm:flex justify-between items-stretch space-y-6 sm:space-y-0',
            !columnLayout && 'sm:space-x-6',
            index !== 0 && !columnLayout && 'border-t-2 pt-4',
            columnLayout && 'col-span-4 sm:flex-col gap-3 mb-5 sm:mb-0',
            layoutClasses
          )}
        >
          {section.schema.map((field) => {
            const valueToDisplay = !field.showMonthlyAnnual
              ? formatKeyMetrics(field, data)
              : [
                  formatKeyMetrics(field, {
                    [field.name]: field.isAnnual
                      ? data[field.name] / 12
                      : data[field.name],
                  }),
                  formatKeyMetrics(field, {
                    [field.name]: field.isAnnual
                      ? data[field.name]
                      : data[field.name] * 12,
                  }),
                ];

            const rawValue = data[field.name];
            const conditionalFormat = field.dynamic
              ? rawValue > 0
                ? 'text-green-700'
                : 'text-red-700'
              : '';

            const textColor = field.textColor ? `text-${field.textColor}` : '';

            // for the sub value formatting
            let subValue;
            let subValueToDisplay;
            let subLabelToDisplay;

            if (field.subName) {
              subValue = data[field.subName];
              subValueToDisplay = formatKeyMetrics(
                { ...field, name: field.subName },
                {
                  [field.subName]: subValue,
                }
              );
              if (field.subConditionalOn === 'subValue') {
                subLabelToDisplay =
                  subValue >= 0 ? field.subLabel[0] : field.subLabel[1];
              } else {
                subLabelToDisplay =
                  rawValue >= 0 ? field.subLabel[0] : field.subLabel[1];
              }
            }

            return (
              <div
                key={field.name}
                className={classNames(
                  'p-3 mx-0 relative my-0 mt-0 mb-0 basis-1/3 grow flex flex-col justify-center items-center text-center text-base rounded-lg bg-white',
                  classes
                )}
              >
                <dt className="leading-7 font-semibold text-black text-md text-center">
                  {Array.isArray(field.label)
                    ? (rawValue > 0 && field.label[0]) || field.label[1]
                    : field.label}
                  {field.description && (
                    <>
                      {' '}
                      <div className="absolute top-0 right-1">
                        <Tooltip
                          text={field.description}
                          heading={field.tooltipHeading}
                          formula={field.tooltipFormula}
                          type="info"
                          classes="ml-2"
                          offset="-left-[13rem] sm:-left-[3rem]"
                        />
                      </div>
                    </>
                  )}
                </dt>

                <dd
                  className={classNames(
                    'font-semibold text-lg tracking-wide text-center',
                    conditionalFormat || textColor || 'text-primary'
                  )}
                >
                  {Array.isArray(valueToDisplay) ? (
                    <div className="flex flex-col gap-[2px]">
                      <span className="text-lg font-semibold">
                        {valueToDisplay[0]}/mo
                      </span>
                      <span className="text-sm font-medium text-black">
                        ({valueToDisplay[1]}/yr)
                      </span>
                    </div>
                  ) : subValue ? (
                    <div className="flex flex-col gap-[2px]">
                      <span className="text-lg font-semibold">
                        {valueToDisplay}
                      </span>
                      <div
                        className={classNames(
                          'text-sm font-medium flex justify-center items-center gap-[2px] text-black'
                        )}
                      >
                        <span className="flex justify-center items-center">
                          (<span>{subLabelToDisplay}</span>
                        </span>
                        <span>{subValueToDisplay})</span>
                      </div>
                    </div>
                  ) : (
                    valueToDisplay
                  )}
                </dd>
              </div>
            );
          })}
        </div>
      ))}
    </dl>
  );
}
